import { Alert, AlertDescription, AlertTitle } from "@coval/ui/alert"
import { Progress } from "@coval/ui/progress"

import type { JudgeConfidence } from "./judgeConfidence"
import { describeDecisionUnavailableReason } from "./legacyDecisionEstimate"

export function JudgeConfidenceDetail({
  confidence,
}: {
  confidence: JudgeConfidence
}) {
  return (
    <div className="space-y-3 text-sm text-coval-text">
      {confidence.status === "ok" &&
        confidence.panelConfidence === "high" &&
        confidence.alignment === "disagrees" && (
          <p>
            Confidence: <span className="font-medium">Medium</span>
          </p>
        )}
      {confidence.panelConfidence && (
        <p>
          Panel agreement:{" "}
          <span className="font-medium capitalize">
            {confidence.panelConfidence}
          </span>
        </p>
      )}
      {confidence.status === "unavailable" ? (
        <p className="text-coval-text-muted">
          {describeDecisionUnavailableReason(confidence.reason ?? "unknown")}
        </p>
      ) : (
        <>
          {confidence.reviewRequired && (
            <Alert variant="warning">
              <AlertTitle>Review needed</AlertTitle>
              <AlertDescription>
                {confidence.alignment === "disagrees"
                  ? `The decision estimate favors ${confidence.mostLikelyAnswers.join(" or ")}, while the judge returned ${confidence.selectedAnswer}.`
                  : confidence.reason === "tied_probabilities"
                    ? `The decision estimate is tied between ${confidence.mostLikelyAnswers.join(" and ")}. It does not identify a single most likely answer.`
                    : confidence.reason === "unscoreable_evidence"
                      ? "Coval Judge could not score this evidence. A probability for UNKNOWN does not establish a passing or failing verdict."
                      : `The judge returned ${confidence.selectedAnswer}, which has no exact match in the decision estimate.`}{" "}
                Panel agreement does not remove this uncertainty.
              </AlertDescription>
            </Alert>
          )}
          <p className="font-medium tabular-nums">
            {confidence.selectedAnswerProbability !== null
              ? `Estimated probability for verdict ${confidence.selectedAnswer}: ${formatPercent(confidence.selectedAnswerProbability)}`
              : `Estimated probability for verdict ${confidence.selectedAnswer}: unavailable`}
          </p>
          <ul aria-label="Answer probabilities" className="space-y-2">
            {confidence.distribution.map((entry) => (
              <li key={entry.label} className="space-y-1">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="min-w-0 break-words text-coval-text-muted">
                    {entry.label}
                  </span>
                  <span className="shrink-0 tabular-nums">
                    {formatPercent(entry.probability)}
                  </span>
                </div>
                <Progress
                  aria-label={`Estimated probability for ${entry.label}`}
                  value={entry.probability * 100}
                />
              </li>
            ))}
          </ul>
          <p className="text-xs text-coval-text-muted">
            Panel agreement describes consistency among the judges. Answer
            probabilities are decision estimates, not a measured accuracy score
            or a guarantee that the verdict is correct.
          </p>
        </>
      )}
    </div>
  )
}

function formatPercent(probability: number): string {
  return `${Math.round(probability * 100)}%`
}
